import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

class PvGhptAddon extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.button = this.querySelector('[data-pv-ghpt-add]');
    this.status = this.querySelector('[data-pv-ghpt-status]');
    this.variantId = Number(this.dataset.variantId);
    this.defaultLabel = this.dataset.defaultLabel || this.button?.textContent?.trim() || 'Pridėti GHPT knygą';
    this.cartUpdateHandler = (event) => this.handleCartUpdate(event);

    this.button?.addEventListener('click', () => this.addGhpt());
    document.addEventListener(StandardEvents.cartLinesUpdate, this.cartUpdateHandler);
    this.refreshState();
  }

  disconnectedCallback() {
    if (this.cartUpdateHandler) {
      document.removeEventListener(StandardEvents.cartLinesUpdate, this.cartUpdateHandler);
    }
  }

  async fetchCartData() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      if (typeof cartItems.fetchCartData === 'function') {
        return cartItems.fetchCartData();
      }
    }

    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Nepavyko nuskaityti krepšelio.');
    return response.json();
  }

  isInCart(cart) {
    return Array.isArray(cart?.items) && cart.items.some((item) => Number(item.variant_id) === this.variantId);
  }

  setState(cart) {
    if (!this.button || !this.status) return;

    if (this.isInCart(cart)) {
      this.button.disabled = true;
      this.button.textContent = '✓ GHPT pridėta į krepšelį';
      this.status.hidden = false;
      this.status.dataset.error = 'false';
      this.status.textContent = 'GHPT knyga jau yra krepšelyje.';
    } else {
      this.button.disabled = false;
      this.button.textContent = this.defaultLabel;
      this.status.hidden = true;
      this.status.dataset.error = 'false';
      this.status.textContent = '';
    }
  }

  async refreshState() {
    try {
      const cart = await this.fetchCartData();
      this.setState(cart);
    } catch (error) {
      console.error('[PV GHPT]', error);
    }
  }

  handleCartUpdate(event) {
    const promise = event?.promise;
    if (promise && typeof promise.then === 'function') {
      promise.then(() => this.refreshState()).catch(() => {});
    } else {
      window.setTimeout(() => this.refreshState(), 150);
    }
  }

  getSectionIds() {
    return [...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((component) => component.dataset.sectionId)
        .filter(Boolean)
    )];
  }

  async addGhpt() {
    if (!this.button || this.button.disabled || !this.variantId) return;

    const originalLabel = this.defaultLabel;
    this.button.disabled = true;
    this.button.textContent = 'Pridedama…';
    this.status.hidden = true;
    this.status.dataset.error = 'false';

    let deferredEventPromise;

    try {
      const cartBefore = await this.fetchCartData();
      if (this.isInCart(cartBefore)) {
        this.setState(cartBefore);
        return;
      }

      const sectionIds = this.getSectionIds();
      deferredEventPromise = CartLinesUpdateEvent.createPromise();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(this.variantId), quantity: 1 }],
          promise: deferredEventPromise.promise,
        })
      );

      const root = window.Shopify?.routes?.root || '/';
      const addUrl = window.Theme?.routes?.cart_add_url || `${root}cart/add.js`;
      const payload = {
        items: [{ id: this.variantId, quantity: 1 }],
      };

      if (sectionIds.length) {
        payload.sections = sectionIds.join(',');
        payload.sections_url = window.location.pathname;
      }

      const response = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();
      if (!response.ok || responseData?.status) {
        throw new Error(responseData?.description || responseData?.message || 'Nepavyko pridėti GHPT knygos.');
      }

      const ajaxCart = await this.fetchCartData();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'pv-ghpt-addon',
          sourceId: this.id || 'pv-ghpt-addon',
          itemCount: 1,
          sections: responseData.sections || {},
          didError: false,
        },
      });

      this.setState(ajaxCart);
    } catch (error) {
      if (deferredEventPromise) deferredEventPromise.reject(error);
      console.error('[PV GHPT]', error);
      this.button.disabled = false;
      this.button.textContent = originalLabel;
      this.status.hidden = false;
      this.status.dataset.error = 'true';
      this.status.textContent = error?.message || 'Nepavyko pridėti GHPT knygos. Bandykite dar kartą.';
    }
  }
}

if (!customElements.get('pv-ghpt-addon')) {
  customElements.define('pv-ghpt-addon', PvGhptAddon);
}

function updateVmvtFinalMicrocopy() {
  const microcopy = document.querySelector('.pvp-final-buy small');
  if (microcopy && microcopy.textContent.includes('GHPT knyga neįtraukta')) {
    microcopy.textContent = 'Skaitmeninę dalį atsiunčiame tą pačią dieną. Fizines prekes išsiunčiame per 1–2 d. d.';
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', updateVmvtFinalMicrocopy, { once: true });
} else {
  updateVmvtFinalMicrocopy();
}
